import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';

function VoucherTypeList({ onAdd, onEdit }) {
    const { hasPermission, fetchVouchers, company } = usePermissionContext();
    const [rows, setRows] = useState([]);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [userRole, setUserRole] = useState('');
    const [selectedIdx, setSelectedIdx] = useState(0);
    const navigate = useNavigate();
    const defaultVoucherTypes = ["Kasar Payment", "Cash Receipt", "cash payment", "Bank Receipt", "Bank Payment"];

    const getRolePrefix = () => {
        const user = JSON.parse(sessionStorage.getItem('user'));
        if (!user || !user.role) return '';
        if (user.role === 'SUPER_ADMIN') return '/super-admin';
        if (user.role === 'ADMIN') return '/admin';
        return '/user';
    };
    const rolePrefix = getRolePrefix();

    // Keyboard navigation: Up, Down, Enter
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (rows.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIdx((prev) => (prev + 1) % rows.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIdx((prev) => (prev - 1 + rows.length) % rows.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const selectedItem = rows[selectedIdx];
                if (selectedItem) {
                    navigate(`${rolePrefix}/settings/voucher-type/ledger/${selectedItem._id}`);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [rows, selectedIdx, navigate, rolePrefix]);

    useEffect(() => {
        const user = JSON.parse(sessionStorage.getItem('user'));
        if (user) setUserRole(user.role);
    }, []);

    const handlePrint = async (voucherType) => {
        try {
            const userStr = sessionStorage.getItem('user');
            if (!userStr) return;
            const user = JSON.parse(userStr);
            const companyId = user.companyId || user.company_id;

            const entriesData = await fetchApi(`/voucher-entry/${voucherType._id}?companyId=${companyId}`);

            // Flatten nested entries
            let flatList = [];
            entriesData.forEach(doc => {
                if (doc.entries && Array.isArray(doc.entries)) {
                    doc.entries.forEach(e => {
                        flatList.push({
                            ...e,
                            parentCreatedAt: doc.createdAt
                        });
                    });
                }
            });

            // Sort by paymentDate
            flatList.sort((a, b) => new Date(a.paymentDate) - new Date(b.paymentDate));

            const printWindow = window.open('', '', 'height=800,width=1000');
            printWindow.document.write('<html><head><title>Voucher Print</title>');
            printWindow.document.write('<style>');
            printWindow.document.write('body { font-family: sans-serif; margin: 40px; color: #000; }');
            printWindow.document.write('.header-card { border: 1px solid #000; text-align: center; margin-bottom: 0px; }');
            printWindow.document.write('.company-name { font-size: 20px; font-weight: bold; border-bottom: 1px solid #000; padding: 5px; text-transform: uppercase; }');
            printWindow.document.write('.company-details { font-size: 13px; border-bottom: 1px solid #000; padding: 4px; }');
            printWindow.document.write('.voucher-type-name { font-size: 14px; font-weight: bold; padding: 4px; border-bottom: 1px solid #000; text-transform: uppercase; background: #f9f9f9; }');
            printWindow.document.write('table { width: 100%; border-collapse: collapse; border: 1px solid #000; }');
            printWindow.document.write('th, td { border: 1px solid #000; padding: 6px 8px; font-size: 12px; text-align: left; }');
            printWindow.document.write('th { background: #fff; font-weight: bold; text-align: center; text-transform: uppercase; }');
            printWindow.document.write('.text-right { text-align: right; }');
            printWindow.document.write('.text-center { text-align: center; }');
            printWindow.document.write('.total-row { font-weight: bold; }');
            printWindow.document.write('</style></head><body>');

            // Header Section
            printWindow.document.write('<div class="header-card">');
            printWindow.document.write(`<div class="company-name">${company?.companyName || ''}</div>`);
            printWindow.document.write(`<div class="company-details">GST NO: ${company?.companyGstNumber || ''}</div>`);
            printWindow.document.write(`<div class="company-details">${company?.companyAddress || ''}, ${company?.companyCity || ''}, ${company?.companyState || ''} ${company?.companyPinCode || ''}</div>`);
            printWindow.document.write(`<div class="voucher-type-name">${voucherType.voucherTypeName}</div>`);
            printWindow.document.write('</div>');

            // Table Section
            printWindow.document.write('<table>');
            printWindow.document.write('<thead><tr>');
            printWindow.document.write('<th width="40">SR NO</th>');
            printWindow.document.write('<th width="90">DATE</th>');
            printWindow.document.write('<th>NARRATION</th>');
            printWindow.document.write('<th width="100">CREDIT</th>');
            printWindow.document.write('<th width="100">DEBIT</th>');
            printWindow.document.write('<th width="100">BALANCE</th>');
            printWindow.document.write('</tr></thead><tbody>');

            let runningBalance = 0;
            let totalCredit = 0;
            let totalDebit = 0;

            flatList.forEach((entry, index) => {
                const credit = entry.paymentType === 'Credit' ? Number(entry.paymentAmount) : 0;
                const debit = entry.paymentType === 'Debit' ? Number(entry.paymentAmount) : 0;

                totalCredit += credit;
                totalDebit += debit;
                runningBalance += (credit - debit);

                const formattedDate = new Date(entry.paymentDate).toLocaleDateString('en-GB');

                printWindow.document.write('<tr>');
                printWindow.document.write(`<td class="text-center">${index + 1}</td>`);
                printWindow.document.write(`<td class="text-center">${formattedDate}</td>`);
                printWindow.document.write(`<td>${entry.account} ${entry.remark ? ` - ${entry.remark}` : ''}</td>`);
                printWindow.document.write(`<td class="text-right">${credit > 0 ? credit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : ''}</td>`);
                printWindow.document.write(`<td class="text-right">${debit > 0 ? debit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : ''}</td>`);
                printWindow.document.write(`<td class="text-right">${runningBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>`);
                printWindow.document.write('</tr>');
            });

            // Total Row
            printWindow.document.write('<tr class="total-row">');
            printWindow.document.write('<td colspan="3" class="text-right">TOTAL</td>');
            printWindow.document.write(`<td class="text-right">${totalCredit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>`);
            printWindow.document.write(`<td class="text-right">${totalDebit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>`);
            printWindow.document.write(`<td class="text-right">${runningBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>`);
            printWindow.document.write('</tr>');

            printWindow.document.write('</tbody></table>');
            printWindow.document.write('</body></html>');
            printWindow.document.close();
            printWindow.print();

        } catch (error) {
            console.error("Print Error:", error);
            toast.error("Failed to generate voucher print");
        }
    };

    const fetchVoucherTypes = async (search = '', page = 1, limit = 10) => {
        try {
            if (!search) setLoading(true);
            const result = await fetchApi(`/voucher-type?search=${search}&page=${page}&limit=${limit}`);

            if (result && result.voucherTypes) {
                setRows(result.voucherTypes);
                setTotalEntries(result.total || 0);
                setTotalPages(result.pages || 1);
            } else if (Array.isArray(result)) {
                setRows(result);
                setTotalEntries(result.length);
                setTotalPages(Math.ceil(result.length / limit));
            }
        } catch (error) {
            console.error('Error fetching voucher types:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchVoucherTypes(searchQuery, currentPage, itemsPerPage);
        }, 200);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, currentPage, itemsPerPage]);

    const handleEdit = (type) => {
        if (onEdit) onEdit(type);
    };

    const handleAdd = () => {
        if (onAdd) onAdd();
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this voucher type?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = sessionStorage.getItem('token');
                    const response = await fetch(`${API_BASE_URL}/voucher-type/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });

                    if (response.ok) {
                        setRows(prevData => prevData.filter(item => item._id !== id));
                        await fetchVouchers();
                        Swal.fire('Deleted!', 'Voucher type has been deleted.', 'success');
                    } else {
                        const errData = await response.json();
                        toast.error(errData.message || 'Failed to delete voucher type');
                    }
                } catch (error) {
                    console.error("Delete Error:", error);
                    toast.error("Server error");
                }
            }
        });
    };

    const showingFrom = totalEntries === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const showingTo = Math.min(totalEntries, (currentPage - 1) * itemsPerPage + rows.length);

    const pages = useMemo(() => {
        const out = [];
        const maxButtons = 5;
        let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
        let end = Math.min(totalPages, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);
        for (let i = start; i <= end; i += 1) out.push(i);
        return out;
    }, [currentPage, totalPages]);

    if (loading) {
        return <PageSkeleton />;
    }

    const columns = [
        {
            key: 'voucherTypeName',
            label: 'VOUCHER NAME',
            render: (item, idx) => (
                <div
                    className={`party-name-box ${idx === selectedIdx ? 'party-name-box--active' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedIdx(idx);
                        navigate(`${rolePrefix}/settings/voucher-type/ledger/${item._id}`);
                    }}
                >
                    {item.voucherTypeName}
                </div>
            )
        },
        { key: 'credit', label: 'CREDIT', style: { textAlign: 'right' }, cellStyle: () => ({ textAlign: 'right' }), render: (item) => item.credit || '0.00' },
        { key: 'debit', label: 'DEBIT', style: { textAlign: 'right' }, cellStyle: () => ({ textAlign: 'right' }), render: (item) => item.debit || '0.00' },
        { key: 'balance', label: 'BALANCE', style: { textAlign: 'right' }, cellStyle: () => ({ textAlign: 'right' }), render: (item) => item.balance || '0.00' },
        {
            key: 'print',
            label: 'PRINT',
            style: { textAlign: 'center' },
            cellStyle: () => ({ textAlign: 'center' }),
            render: (item) => (
                <button
                    className="list-page-btn-bundle"
                    style={{ backgroundColor: '#0b3a54', fontSize: '10px' }}
                    onClick={() => handlePrint(item)}
                >
                    Voucher Print
                </button>
            )
        },
        {
            key: 'actions',
            label: 'ACTION',
            style: { textAlign: 'center' },
            cellStyle: () => ({ textAlign: 'center' }),
            render: (item) => (
                <div className="list-page-action-icons" style={{ justifyContent: 'center', gap: '8px' }}>
                    {hasPermission('VoucherType', 'update') && !defaultVoucherTypes.some(d => d.toLowerCase() === item.voucherTypeName?.toLowerCase()) && (
                        <button
                            type="button"
                            className="list-page-icon-btn"
                            title="Update"
                            onClick={() => handleEdit(item)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                    )}
                    {hasPermission('VoucherType', 'delete') && !defaultVoucherTypes.some(d => d.toLowerCase() === item.voucherTypeName?.toLowerCase()) && (
                        <button
                            type="button"
                            className="list-page-icon-btn"
                            title="Delete"
                            onClick={() => handleDelete(item._id)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                    )}
                </div>
            )
        }
    ];

    const footerRow = rows.length > 0 ? (
        <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
            <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
            <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
                {rows.reduce((sum, item) => sum + (parseFloat(item.credit) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </td>
            <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
                {rows.reduce((sum, item) => sum + (parseFloat(item.debit) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </td>
            <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
                {rows.reduce((sum, item) => sum + (parseFloat(item.balance) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </td>
            <td></td>
            <td></td>
        </tr>
    ) : null;

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">Voucher Type List</h1>
                <div className="list-page-action-buttons">
                    {hasPermission('VoucherType', 'add') && (
                        <button className="list-page-btn list-page-btn-add" onClick={handleAdd}>
                            ADD VOUCHER TYPE
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={rows}
                loading={loading}
                emptyMessage="No records found"
                page={currentPage}
                pageSize={itemsPerPage}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setItemsPerPage}
                footerRow={footerRow}
                onRowClick={(item, idx) => {
                    setSelectedIdx(idx);
                }}
                rowClassName={(item, idx) => idx === selectedIdx ? 'selected-row' : ''}
                searchBar={
                    <SearchBar
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Search voucher types..."
                    />
                }
            />
        </div>
    );
}

export default VoucherTypeList;
